import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, OnDestroy } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { FareRuleCategory, FinnairBoundItem, FinnairPassengerItem } from '@fcom/dapi/api/models';
import { LoaderTheme } from '@fcom/ui-components';
import { unsubscribe } from '@fcom/core/utils';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { CartFareRules, PartialCartFareRuleRecord } from '@fcom/dapi';
import { finShare } from '@fcom/rx';

import { BookingCartService } from '../../../../services';
import { cartIdAndHash, fareRules } from '../../../../store/selectors';

@Component({
  selector: 'fin-purchase-review-fare-rules',
  templateUrl: './purchase-review-fare-rules.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PurchaseReviewFareRulesComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  passengers: FinnairPassengerItem[];

  @Input({ required: true })
  cartId: string;

  @Input({ required: true })
  bound: FinnairBoundItem;

  rulesExpandersOpen: boolean[];
  fareRules$: Observable<CartFareRules>;
  activePaxId$: BehaviorSubject<string> = new BehaviorSubject(null);

  readonly FareRuleCategory = FareRuleCategory;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private bookingCartService: BookingCartService,
    private cdr: ChangeDetectorRef,
    private store$: Store<BookingAppState>
  ) {}

  ngOnInit(): void {
    this.activePaxId$.next(this.passengers[0].id);

    this.fareRules$ = combineLatest([this.store$.pipe(fareRules()), this.activePaxId$]).pipe(
      map(([fareRules, activePaxId]): CartFareRules => {
        if (!this.bound) {
          return fareRules;
        }

        const rulesPerPax = fareRules[activePaxId]?.ticketRules || {};

        const flightIdsInBound = this.getFlightIdsInBound();

        const newTicketRules = Object.keys(rulesPerPax).reduce(
          (fareRules, category: FareRuleCategory) => ({
            ...fareRules,
            [category]: rulesPerPax[category].filter((rule) => flightIdsInBound.includes(rule.flightId)),
          }),
          {} as PartialCartFareRuleRecord
        );

        return {
          [activePaxId]: {
            ...fareRules[activePaxId],
            ticketRules: newTicketRules,
          },
        };
      }),
      finShare()
    );

    this.rulesExpandersOpen = new Array(Object.keys(FareRuleCategory).length).fill(false);
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  getFlightIdsInBound(): string[] {
    return this.bound.itinerary
      .map((itineraryItem) => {
        return isFlight(itineraryItem) ? itineraryItem.id : null;
      })
      .filter(Boolean);
  }

  handlePassengerTabChange(paxId: unknown): void {
    this.activePaxId$.next(paxId as string);
    this.closeAllRulesExpanders();
    this.cdr.detectChanges();
  }

  closeAllRulesExpanders(): void {
    this.rulesExpandersOpen = this.rulesExpandersOpen.map(() => false);
  }

  handleRulesExpanderToggle(index: number, cartId: string, travellerId: string, category: string): void {
    this.rulesExpandersOpen[index] = !this.rulesExpandersOpen[index];

    this.subscriptions.add(
      combineLatest([this.fareRules$, this.store$.pipe(cartIdAndHash())])
        .pipe(take(1))
        .subscribe(([fareRules, cartHash]) => {
          const rulesInStore = fareRules[travellerId]?.ticketRules[category];
          const rulesLoadingOrError =
            fareRules[travellerId] &&
            (fareRules[travellerId]?.isLoading?.includes(category as FareRuleCategory) ||
              fareRules[travellerId]?.hasError?.includes(category as FareRuleCategory));

          if (rulesInStore || rulesLoadingOrError) {
            return;
          }
          this.bookingCartService.triggerTicketRules(cartId, travellerId, category as FareRuleCategory, cartHash.hash);
        })
    );
  }
}
